import { Component, Input, Inject, ViewChild, OnInit } from '@angular/core';
import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CdkTextareaAutosize, TextFieldModule } from '@angular/cdk/text-field';
import { NoteDialogConfiguration } from 'src/app/shared/models/note-dialog-configuration.model';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';
import { NoteDocument } from 'src/app/compliance/models/posted-documents-models';
import {
    MatLegacyTableDataSource as MatTableDataSource,
    MatLegacyTableModule as MatTableModule
} from '@angular/material/legacy-table';
import { DocumentService } from 'src/app/core/document.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';

@UntilDestroy()
@Component({
    selector: 'nova-note-dialog',
    templateUrl: './note-dialog.component.html',
    styleUrls: ['./note-dialog.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        MatIconModule,
        MatFormFieldModule,
        MatDialogModule,
        MatDividerModule,
        MatTableModule,
        MatCheckboxModule,
        MatInputModule,
        TextFieldModule,
        FormsModule,
        MatButtonModule,
        ReactiveFormsModule
    ]
})
export class NoteDialogComponent implements OnInit {
    @Input() title = 'Add Note';
    @Input() note: string;
    @Input() maxLength: number = null;
    @Input() readonly: boolean = false;
    @Input() saveBtnText?: string = 'SAVE';

    needToAttachToDocument = false;

    @ViewChild('autosize')
    autosize: CdkTextareaAutosize;

    hasValidationErrors: boolean = false;
    editMode = false;

    docSearch = new UntypedFormControl('');

    displayedColumns = ['select', 'docCategory', 'isAssigned', 'docName', 'percent'];

    documents = new MatTableDataSource<NoteDocument>([]);
    selection = new SelectionModel<NoteDocument>(true, []);

    constructor(
        @Inject(MAT_DIALOG_DATA) private readonly _data: NoteDialogConfiguration,
        private readonly _documentService: DocumentService
    ) {}

    ngOnInit(): void {
        this.title = this._data.title;
        this.saveBtnText = this._data && 'saveBtnText' in this._data ? this._data.saveBtnText : 'SAVE';
        this.note = this._data.note;

        if (this._data.note) {
            this.editMode = true;
        }

        // AN-12137 - increase max length to 1500 characters
        this.maxLength = this._data && 'maxLength' in this._data ? this._data.maxLength : 1500;
        this.readonly = this._data && 'readonly' in this._data ? this._data.readonly : false;
        this.needToAttachToDocument = this._data.needToAttachToDocument ?? false;

        if (this.needToAttachToDocument) {
            this.documents.data = this._data.documents;

            this.docSearch.valueChanges.pipe(untilDestroyed(this)).subscribe((text: string) => {
                this.documents.filter = text.toLowerCase();
            });
        }
    }

    noteChanged() {
        this.hasValidationErrors =
            this.maxLength && this.note && this.maxLength > 0 && this.note.length >= this.maxLength;
    }

    showFile(documentId: number) {
        // TODO: Move to an effect
        this._documentService.openDocument(documentId, 'assigned-file').subscribe();
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.documents.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        this.isAllSelected()
            ? this.selection.clear()
            : this.documents.data.forEach((row) => this.selection.select(row));
    }

    isSaveDisabled(): boolean {
        if (this.needToAttachToDocument) {
            return !this.note || this.selection.selected.length === 0;
        } else {
            return !this.note && !this.editMode;
        }
    }

    getResult(cancelled = false): unknown {
        if (cancelled) {
            return null;
        }

        if (this.needToAttachToDocument) {
            return {
                docNote: this.note,
                selectedDocumentIds: this.selection.selected.map((doc) => doc.candidateDocumentId)
            };
        } else {
            return this.note;
        }
    }
}
